import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddDeliveryRegionOptions,
  AddDeliveryRegionResponse,
  CreateShippoConfigurationOptions,
  QueryShippoConfigurationsResponse,
  RemoveDeliveryRegionIdentifiers,
  RemoveDeliveryRegionOptions,
  RemoveDeliveryRegionResponse,
  ShippoConfiguration,
  ShippoConfigurationCreatedEnvelope,
  ShippoConfigurationDeletedEnvelope,
  ShippoConfigurationQuery,
  ShippoConfigurationUpdatedEnvelope,
  ShippoConfigurationsQueryBuilder,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsResponse,
  UpdateShippoConfiguration,
  addDeliveryRegion as universalAddDeliveryRegion,
  createShippoConfiguration as universalCreateShippoConfiguration,
  deleteShippoConfiguration as universalDeleteShippoConfiguration,
  getShippoConfiguration as universalGetShippoConfiguration,
  queryShippoConfigurations as universalQueryShippoConfigurations,
  removeDeliveryRegion as universalRemoveDeliveryRegion,
  typedQueryShippoConfigurations as universalTypedQueryShippoConfigurations,
  updateExtendedFields as universalUpdateExtendedFields,
  updateShippoConfiguration as universalUpdateShippoConfiguration,
} from './ecom-v1-shippo-configuration-shippo-configurations.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function createShippoConfiguration(
  httpClient: HttpClient
): CreateShippoConfigurationSignature {
  return (
    shippoConfiguration: NonNullablePaths<
      ShippoConfiguration,
      | `deliveryRegionId`
      | `osLocationId`
      | `packageDetails`
      | `packageDetails.dimensions.height`
      | `packageDetails.dimensions.length`
      | `packageDetails.dimensions.width`
      | `packageType`,
      4
    >,
    options?: CreateShippoConfigurationOptions
  ) =>
    universalCreateShippoConfiguration(
      shippoConfiguration,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateShippoConfigurationSignature {
  /**
   * Creates a ShippoConfiguration.
   * @param - ShippoConfiguration to be created.
   * @returns The created ShippoConfiguration.
   */
  (
    shippoConfiguration: NonNullablePaths<
      ShippoConfiguration,
      | `deliveryRegionId`
      | `osLocationId`
      | `packageDetails`
      | `packageDetails.dimensions.height`
      | `packageDetails.dimensions.length`
      | `packageDetails.dimensions.width`
      | `packageType`,
      4
    >,
    options?: CreateShippoConfigurationOptions
  ): Promise<
    NonNullablePaths<
      ShippoConfiguration,
      | `domesticServices`
      | `domesticServices.${number}.service`
      | `domesticServices.${number}.serviceSettings.handlingFee.value`
      | `domesticServices.${number}.serviceSettings.handlingFee.calculationType`
      | `internationalServices`
      | `internationalServices.${number}.service`
      | `packageType`
      | `packageDetails.dimensions.length`
      | `packageDetails.dimensions.width`
      | `packageDetails.dimensions.height`
      | `shipFromAddress.streetAddress.number`
      | `shipFromAddress.streetAddress.name`
      | `deliveryRegionIds`,
      6
    >
  >;
}

export function getShippoConfiguration(
  httpClient: HttpClient
): GetShippoConfigurationSignature {
  return (shippoConfigurationId: string) =>
    universalGetShippoConfiguration(
      shippoConfigurationId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetShippoConfigurationSignature {
  /**
   * Retrieves a ShippoConfiguration.
   * @param - ID of the ShippoConfiguration to retrieve.
   * @returns The requested ShippoConfiguration.
   */
  (shippoConfigurationId: string): Promise<
    NonNullablePaths<
      ShippoConfiguration,
      | `domesticServices`
      | `domesticServices.${number}.service`
      | `domesticServices.${number}.serviceSettings.handlingFee.value`
      | `domesticServices.${number}.serviceSettings.handlingFee.calculationType`
      | `internationalServices`
      | `internationalServices.${number}.service`
      | `packageType`
      | `packageDetails.dimensions.length`
      | `packageDetails.dimensions.width`
      | `packageDetails.dimensions.height`
      | `shipFromAddress.streetAddress.number`
      | `shipFromAddress.streetAddress.name`
      | `deliveryRegionIds`,
      6
    >
  >;
}

export function updateShippoConfiguration(
  httpClient: HttpClient
): UpdateShippoConfigurationSignature {
  return (
    _id: string,
    shippoConfiguration: NonNullablePaths<
      UpdateShippoConfiguration,
      `revision`,
      2
    >
  ) =>
    universalUpdateShippoConfiguration(
      _id,
      shippoConfiguration,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateShippoConfigurationSignature {
  /**
   * Updates a ShippoConfiguration.
   * @param - ShippoConfiguration ID.
   * @returns Updated ShippoConfiguration.
   */
  (
    _id: string,
    shippoConfiguration: NonNullablePaths<
      UpdateShippoConfiguration,
      `revision`,
      2
    >
  ): Promise<
    NonNullablePaths<
      ShippoConfiguration,
      | `domesticServices`
      | `domesticServices.${number}.service`
      | `domesticServices.${number}.serviceSettings.handlingFee.value`
      | `domesticServices.${number}.serviceSettings.handlingFee.calculationType`
      | `internationalServices`
      | `internationalServices.${number}.service`
      | `packageType`
      | `packageDetails.dimensions.length`
      | `packageDetails.dimensions.width`
      | `packageDetails.dimensions.height`
      | `shipFromAddress.streetAddress.number`
      | `shipFromAddress.streetAddress.name`
      | `deliveryRegionIds`,
      6
    >
  >;
}

export function deleteShippoConfiguration(
  httpClient: HttpClient
): DeleteShippoConfigurationSignature {
  return (shippoConfigurationId: string) =>
    universalDeleteShippoConfiguration(
      shippoConfigurationId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteShippoConfigurationSignature {
  /**
   * Deletes a ShippoConfiguration.
   * @param - ID of the ShippoConfiguration to delete.
   */
  (shippoConfigurationId: string): Promise<void>;
}

export function queryShippoConfigurations(
  httpClient: HttpClient
): QueryShippoConfigurationsSignature {
  return () =>
    universalQueryShippoConfigurations(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryShippoConfigurationsSignature {
  /**
   * Retrieves a list of ShippoConfigurations, given the provided [paging, filtering, and sorting][1].
   *
   * Up to 1,000 ShippoConfigurations can be returned per request.
   *
   * To learn how to query ShippoConfigurations, see [API Query Language][2].
   *
   * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
   * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
   */
  (): ShippoConfigurationsQueryBuilder;
}

export function typedQueryShippoConfigurations(
  httpClient: HttpClient
): TypedQueryShippoConfigurationsSignature {
  return (query: ShippoConfigurationQuery) =>
    universalTypedQueryShippoConfigurations(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryShippoConfigurationsSignature {
  /** */
  (query: ShippoConfigurationQuery): Promise<
    NonNullablePaths<
      QueryShippoConfigurationsResponse,
      | `shippoConfigurations`
      | `shippoConfigurations.${number}.packageType`
      | `shippoConfigurations.${number}.packageDetails.dimensions.length`
      | `shippoConfigurations.${number}.packageDetails.dimensions.width`
      | `shippoConfigurations.${number}.packageDetails.dimensions.height`
      | `shippoConfigurations.${number}.shipFromAddress.streetAddress.number`
      | `shippoConfigurations.${number}.shipFromAddress.streetAddress.name`,
      6
    >
  >;
}

export function updateExtendedFields(
  httpClient: HttpClient
): UpdateExtendedFieldsSignature {
  return (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ) =>
    universalUpdateExtendedFields(
      _id,
      namespace,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateExtendedFieldsSignature {
  /**
   * Updates extended fields of a ShippoConfiguration without incrementing revision
   * @param - ID of the entity to update.
   * @param - Identifier for the app whose extended fields are being updated.
   */
  (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ): Promise<
    NonNullablePaths<
      UpdateExtendedFieldsResponse,
      | `shippoConfiguration.domesticServices`
      | `shippoConfiguration.domesticServices.${number}.service`
      | `shippoConfiguration.domesticServices.${number}.serviceSettings.handlingFee.value`
      | `shippoConfiguration.domesticServices.${number}.serviceSettings.handlingFee.calculationType`
      | `shippoConfiguration.internationalServices`
      | `shippoConfiguration.internationalServices.${number}.service`
      | `shippoConfiguration.packageType`
      | `shippoConfiguration.packageDetails.dimensions.length`
      | `shippoConfiguration.packageDetails.dimensions.width`
      | `shippoConfiguration.packageDetails.dimensions.height`
      | `shippoConfiguration.shipFromAddress.streetAddress.number`
      | `shippoConfiguration.shipFromAddress.streetAddress.name`
      | `shippoConfiguration.deliveryRegionIds`,
      7
    >
  >;
}

export function addDeliveryRegion(
  httpClient: HttpClient
): AddDeliveryRegionSignature {
  return (
    shippoConfigurationId: string,
    deliveryRegionId: string,
    options?: AddDeliveryRegionOptions
  ) =>
    universalAddDeliveryRegion(
      shippoConfigurationId,
      deliveryRegionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddDeliveryRegionSignature {
  /**
   * Adds a delivery region to a Shippo configuration.
   * @param - ID of the ShippoConfiguration to add the delivery region to.
   * @param - ID of the delivery region to add.
   */
  (
    shippoConfigurationId: string,
    deliveryRegionId: string,
    options?: AddDeliveryRegionOptions
  ): Promise<
    NonNullablePaths<
      AddDeliveryRegionResponse,
      | `shippoConfiguration.domesticServices`
      | `shippoConfiguration.domesticServices.${number}.service`
      | `shippoConfiguration.domesticServices.${number}.serviceSettings.handlingFee.value`
      | `shippoConfiguration.domesticServices.${number}.serviceSettings.handlingFee.calculationType`
      | `shippoConfiguration.internationalServices`
      | `shippoConfiguration.internationalServices.${number}.service`
      | `shippoConfiguration.packageType`
      | `shippoConfiguration.packageDetails.dimensions.length`
      | `shippoConfiguration.packageDetails.dimensions.width`
      | `shippoConfiguration.packageDetails.dimensions.height`
      | `shippoConfiguration.shipFromAddress.streetAddress.number`
      | `shippoConfiguration.shipFromAddress.streetAddress.name`
      | `shippoConfiguration.deliveryRegionIds`,
      7
    >
  >;
}

export function removeDeliveryRegion(
  httpClient: HttpClient
): RemoveDeliveryRegionSignature {
  return (
    identifiers: NonNullablePaths<
      RemoveDeliveryRegionIdentifiers,
      `deliveryRegionId` | `shippoConfigurationId`,
      2
    >,
    options?: RemoveDeliveryRegionOptions
  ) =>
    universalRemoveDeliveryRegion(
      identifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveDeliveryRegionSignature {
  /**
   * Removes a delivery region from a Shippo configuration.
   * When passing the ID of the only existing delivery region, removing it will delete the Shippo configuration.
   */
  (
    identifiers: NonNullablePaths<
      RemoveDeliveryRegionIdentifiers,
      `deliveryRegionId` | `shippoConfigurationId`,
      2
    >,
    options?: RemoveDeliveryRegionOptions
  ): Promise<
    NonNullablePaths<
      RemoveDeliveryRegionResponse,
      | `shippoConfiguration.domesticServices`
      | `shippoConfiguration.domesticServices.${number}.service`
      | `shippoConfiguration.domesticServices.${number}.serviceSettings.handlingFee.value`
      | `shippoConfiguration.domesticServices.${number}.serviceSettings.handlingFee.calculationType`
      | `shippoConfiguration.internationalServices`
      | `shippoConfiguration.internationalServices.${number}.service`
      | `shippoConfiguration.packageType`
      | `shippoConfiguration.packageDetails.dimensions.length`
      | `shippoConfiguration.packageDetails.dimensions.width`
      | `shippoConfiguration.packageDetails.dimensions.height`
      | `shippoConfiguration.shipFromAddress.streetAddress.number`
      | `shippoConfiguration.shipFromAddress.streetAddress.name`
      | `shippoConfiguration.deliveryRegionIds`,
      7
    >
  >;
}

export const onShippoConfigurationCreated = EventDefinition(
  'wix.ecom.v1.shippo_configuration_created',
  true,
  (event: ShippoConfigurationCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'entity.shipFromAddress' }],
        },
      ])
    )
)<ShippoConfigurationCreatedEnvelope>();
export const onShippoConfigurationDeleted = EventDefinition(
  'wix.ecom.v1.shippo_configuration_deleted',
  true,
  (event: ShippoConfigurationDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'entity.shipFromAddress' }],
        },
      ])
    )
)<ShippoConfigurationDeletedEnvelope>();
export const onShippoConfigurationUpdated = EventDefinition(
  'wix.ecom.v1.shippo_configuration_updated',
  true,
  (event: ShippoConfigurationUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.shipFromAddress' },
            { path: 'modifiedFields.shipFromAddress' },
          ],
        },
      ])
    )
)<ShippoConfigurationUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AddDeliveryRegionOptions,
  AddDeliveryRegionRequest,
  AddDeliveryRegionResponse,
  Address,
  AddressLocation,
  BaseEventMetadata,
  CalculationType,
  CreateShippoConfigurationOptions,
  CreateShippoConfigurationRequest,
  CreateShippoConfigurationResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteShippoConfigurationRequest,
  DeleteShippoConfigurationResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  DomesticService,
  DomesticServiceSettings,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  GetShippoConfigurationRequest,
  GetShippoConfigurationResponse,
  HandlingFee,
  IdentificationData,
  IdentificationDataIdOneOf,
  InternationalService,
  InternationalServiceSettings,
  MessageEnvelope,
  PackageDetails,
  PackageDimensions,
  PackageType,
  QueryShippoConfigurationsRequest,
  QueryShippoConfigurationsResponse,
  RemoveDeliveryRegionIdentifiers,
  RemoveDeliveryRegionOptions,
  RemoveDeliveryRegionRequest,
  RemoveDeliveryRegionResponse,
  RestoreInfo,
  ServiceSettings,
  ShippoConfiguration,
  ShippoConfigurationCreatedEnvelope,
  ShippoConfigurationDeletedEnvelope,
  ShippoConfigurationQuerySpec,
  ShippoConfigurationUpdatedEnvelope,
  ShippoConfigurationsQueryBuilder,
  ShippoConfigurationsQueryResult,
  SortOrder,
  Sorting,
  StreetAddress,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  UpdateShippoConfiguration,
  UpdateShippoConfigurationRequest,
  UpdateShippoConfigurationResponse,
  WebhookIdentityType,
  utils,
} from './ecom-v1-shippo-configuration-shippo-configurations.universal.js';
